/**
 * The service catalogue (§7) — one full record per supplied service.
 *
 * `content/site.ts` holds the service *names*, and it holds nothing else: a
 * name and an icon, which is all the home strip and the /about band ever
 * needed. This file is what `/services` and `/services/:slug` read, and it is
 * the names with a page's worth of substance attached — what the service
 * covers, how it runs, what it is bounded by, what it leaves you with, and
 * the questions a Ugandan buyer actually asks about it.
 *
 * It does not restate the names. `serviceCatalogue` is keyed by `ServiceSlug`
 * and every record's `title` is typed as `ServiceTitle`, the literal union of
 * what §7 supplied, so the two lists cannot drift: rename a service in
 * `site.ts` and this file stops compiling, add one and `titleToSlug` below
 * stops compiling until it has a page.
 *
 * ## Renamed and merged 2026-10-21
 *
 * Four names changed and two records became one. `site.ts` carries the
 * reasoning for the names; what matters here is the merge. `tax-finance` and
 * `payment-support` were one continuous subject — what an import costs you
 * and when each part falls due — cut in half at the point where the money
 * stops going to Kabex and starts going to URA. That is a line on our ledger,
 * not in the buyer's experience, and each page spent its FAQ answering a
 * question the other page raised. They are now `tax-financing`, and both old
 * slugs 301 to it in `public/.htaccess`.
 *
 * No claim was invented to make the merge work. Every sentence in the merged
 * record came from one of the two it replaced, or from `content/paymentTerms`.
 *
 * ## Where the facts come from
 *
 * Everything load-bearing is already confirmed somewhere in this project:
 *
 *   - 30% or 50% of CIF to start, the balance payable in instalments and due
 *     two weeks before the car reaches port — A12, confirmed by Kabex
 *     2026-09-17 and revised by Kabex 2026-10-21. The figures are NOT typed
 *     into this file; they are interpolated from `content/paymentTerms.ts`,
 *     which is also what `importSteps` reads.
 *   - The URA rates, fees, age limits and the valuation database — confirmed
 *     with Kabex management 2026-09-20, see docs/ura-tax-methodology.md.
 *   - Inland transport rates for Kampala, Bunagana (DR Congo) and Nimule
 *     (South Sudan), which are package options rather than a promise that
 *     every order includes onward transport.
 *   - The Kampala Road office — §14, `config/company.ts`.
 *
 * ## Where the figures are not
 *
 * Deliberately no shilling amounts and no percentages in this file, and the
 * rule is `content/guides.ts`'s rather than a new one: an amount typed into
 * copy is a figure that dates on a page nobody remembers to edit. The rates
 * and fees live in Kabex Hub settings, the calculators read them from there,
 * and these services point at the calculators. The one exception is the
 * deposit split, because that is a commercial term rather than a tax rate —
 * and even that is interpolated from `content/paymentTerms.ts` rather than
 * written out, so the term cannot survive here after it has changed there.
 *
 * `cost` is the same rule applied to the hub: it is a *signal* and never an
 * amount. "Free", "quoted per job" and "from the deposit" are each already
 * stated in the copy below, and a reader who wants the number is sent to a
 * calculator or a quotation. See `ServiceCost`.
 *
 * ## PENDING KABEX (§59)
 *
 * The names are approved. The prose beneath each of them is ours, and is
 * drafted from the material above rather than invented — but it has not been
 * through Kabex, and three records make claims that need confirming before
 * this ships, each marked `NEEDS KABEX` at the line:
 *
 *   - `local-vehicle-registration`: that Kabex registers in the buyer's own
 *     name rather than transferring from a company registration.
 *   - `car-importation`: that trucks, buses and other commercial units are
 *     sourced at all (added 2026-10-18 with the Chinese market and Sinotruk).
 *     Nothing names a tonnage, a body or a lead time.
 */

import {
  balance,
  BALANCE_DEADLINE,
  deposit,
  DEPOSIT_RANGE,
  installments,
  ownership,
  PORT_ARRIVAL,
} from './paymentTerms';
import { type IconKey, type ServiceTitle } from './site';

/** The three stages a buyer meets the catalogue in. */
export type ServicePillarId = 'find' | 'ship' | 'pay';

export type ServicePillar = {
  id: ServicePillarId;
  /** Decorative numeral; the pillar's position is already in the markup. */
  step: string;
  title: string;
  /** What this group of services is for, in one sentence. */
  text: string;
  icon: IconKey;
};

/**
 * The catalogue's three groups, in the order an import is actually lived.
 *
 * Seven equal tiles is what /about and the home page already show, and it is
 * why neither reads as anything but a list: with no grouping, the last
 * service argues with the first for the same attention and a visitor cannot
 * tell which two of them apply to them today. Grouped, the page answers
 * "where am I" before it answers "what do you sell".
 *
 * The groups fall 2/3/2, which is simply where the services land; a four-way
 * grouping balanced better on paper and put registration in a pillar of its
 * own, which overstated it.
 */
export const servicePillars: readonly ServicePillar[] = [
  {
    id: 'find',
    step: '01',
    title: 'Find the right car',
    text: 'Before any money moves: what to buy, whether it can still be imported, and what it will really cost to put on the road.',
    icon: 'search',
  },
  {
    id: 'ship',
    step: '02',
    title: 'Bring it in and agree the handover',
    text: 'Your order records the CIF destination and the services included in your package.',
    icon: 'route',
  },
  {
    id: 'pay',
    step: '03',
    title: 'Plan and meet the cost',
    text: 'What URA will charge, when each payment falls due, and how the duty bill at clearing gets met.',
    icon: 'payment',
  },
];

export type ServiceSlug =
  | 'car-importation'
  | 'import-consultation'
  | 'clearing-forwarding-and-logistics'
  | 'local-vehicle-registration'
  | 'tax-inquiries-and-quotations'
  | 'tax-financing';

/**
 * A short labelled figure, shown as a row in the detail page's aside and — for
 * the first three — as the opening band's trust strip.
 *
 * Carries its own icon rather than borrowing the service's. The strip draws an
 * icon beside every label, so a fact without one would have meant three copies
 * of the same mark in a row, which reads as a rendering bug rather than as
 * three claims.
 */
export type ServiceFact = { icon: IconKey; label: string; value: string };

export type ServiceFaq = { question: string; answer: string };

/**
 * Structurally what `NextStepList` draws, declared here rather than imported
 * from `components/molecules` so the catalogue stays free of component
 * imports — content should not know what renders it.
 */
export type ServiceStep = { title: string; text: string };

/**
 * What this service costs, as a signal rather than a number.
 *
 * Added 2026-10-21 with the hub's comparison table, and it exists because of
 * the one complaint a services page reliably earns: a visitor reads seven
 * descriptions and still cannot tell which of them are free, which come with
 * the import and which are a separate bill. Answering that in the grid is
 * worth more than another adjective, and it filters the enquiries that reach
 * the office down to the ones worth having.
 *
 * `tone` is the chip's colour and `label` is its words; `note` is the one
 * clause that stops the label being read as more than it says. No amount
 * appears in any of the three — see the file header. Every label below is a
 * restatement of something the record's own copy already says.
 */
export type ServiceCostTone = 'free' | 'quoted' | 'deposit';

export type ServiceCost = {
  tone: ServiceCostTone;
  /** Two or three words. Shown in a chip, so it has to survive a card. */
  label: string;
  /** The qualifier, where the label alone would overclaim. */
  note?: string;
};

export type Service = {
  slug: ServiceSlug;
  /** Exactly the §7 name. Typed so a rename there breaks the build here. */
  title: ServiceTitle;
  icon: IconKey;
  pillar: ServicePillarId;
  /**
   * The hub card's one line. Written to be read on its own in a grid, so it
   * says what the service *does for you* rather than restating the title in a
   * sentence.
   */
  tagline: string;
  /**
   * What you are left holding when this service is finished.
   *
   * The one line in the record written as a result rather than as an
   * activity, and the reason it is a separate field instead of a better
   * `tagline`: the comparison table needs a column that answers "what do I
   * get", and a tagline answers "what is this". A reader scanning seven rows
   * is choosing between outcomes.
   */
  outcome: string;
  cost: ServiceCost;
  /** The detail page's lede, and the hub card's body. */
  summary: string;
  metaDescription: string;
  /** The detail page's opening prose. */
  overview: readonly string[];
  /** What the service covers — the page's most-read block. */
  includes: readonly string[];
  /** How this one service runs, in order. */
  steps: readonly ServiceStep[];
  /** The aside: three or four things a buyer wants without reading. */
  facts: readonly ServiceFact[];
  /** Rendered on the page and fed to `faqSchema` from the same array. */
  faqs: readonly ServiceFaq[];
  /** Where a reader who would rather not talk to anyone is sent. */
  tool?: { label: string; to: string };
  /** Prefills WhatsApp with this service's own context (§60 K/L). */
  whatsappMessage: string;
};

/* The three cost signals, written once. Repeating the words record by record
   is how two services end up describing the same commercial position in two
   different phrases, which reads to a visitor as two different positions. */
const FREE: ServiceCost = {
  tone: 'free',
  label: 'Free',
  note: 'Not conditional on importing through us',
};

const QUOTED: ServiceCost = {
  tone: 'quoted',
  label: 'Quoted per job',
  note: 'Priced before the work starts, never after',
};

const FROM_DEPOSIT: ServiceCost = {
  tone: 'deposit',
  label: `Starts at ${DEPOSIT_RANGE} of CIF`,
  note: `Balance in instalments, due ${BALANCE_DEADLINE}`,
};

const NO_KABEX_FEE: ServiceCost = {
  tone: 'free',
  label: 'No Kabex fee',
  note: 'Finance partner fees may apply',
};

const bySlug: Record<ServiceSlug, Service> = {
  /* ---------------------------------------------------------------- find */

  'car-importation': {
    slug: 'car-importation',
    title: 'Car importation',
    icon: 'car',
    pillar: 'find',
    tagline: 'The exact car you specify, bought abroad and landed in your own name.',
    outcome:
      'The car you chose, handed over at the agreed CIF destination after the services in your package are complete.',
    cost: FROM_DEPOSIT,
    summary:
      'Name the make, model, year, trim and budget, and we source that car to order from markets around the world. Your quotation records the CIF destination and whether clearing, registration or inland transport is included.',
    metaDescription:
      'Import a car to Uganda with Kabex Motors, with your CIF destination and package services recorded in the quotation.',
    overview: [
      'Kabex runs no showroom floor and no bond, and that is the offer rather than a limitation. Every car we sell is one we buy to order, so the car that arrives is the one you described — not the nearest thing somebody already had standing on a lot, bought months ago on somebody else’s judgement of what you would want.',
      'It changes how the price reads, and that is worth understanding before you commit. An import is quoted as CIF — the car, the insurance and the freight — and URA assesses the tax separately once it lands. One cost to you, two bills on paper. It is why the calculators on this site show both: so the whole figure is in front of you before you pay any of it, rather than after the car is sitting at Mombasa.',
      'What you are buying from us, in the end, is a chain of decisions made by somebody who makes them every week. Which market has the model you want. Which year crosses the line into an extra levy. Get those wrong on your own and the saving on the car is gone before it reaches the border.',
    ],
    includes: [
      'Cars sourced to order worldwide, with Japan, China, the UK, South Korea, Germany and the United States among common markets',
      // NEEDS KABEX: that commercials are sourced at all, and from where. Added
      // 2026-10-18 with the Chinese market, which is the route Sinotruk comes
      // on; no tonnage, body or lead time is claimed, because none is known.
      'Trucks, buses and other commercial units from China, alongside passenger cars',
      'Make, model, year, mileage, trim and known faults confirmed in writing before you pay',
      'Auction sheets and inspection reports wherever the exporting market issues them',
      'A JEVIC pre-shipment inspection for each vehicle',
      'Photographs and video of the actual unit, since the car is still abroad when you choose it',
      'One named sales officer from first enquiry through to handover',
      'Clearing, registration, transport and plates where included in your package',
      'Handover at the CIF destination agreed in your order — this may be Mombasa, Kabex’s Kampala office, Nimule, Bunagana or another agreed destination',
    ],
    steps: [
      {
        title: 'Tell us the car',
        text: 'Make, model, year and budget. If you are not sure, tell us what you need the car for and we will shortlist against that instead.',
      },
      {
        title: 'See what we can source',
        text: 'We come back with the units we can buy for you now — each with photographs, mileage, an auction sheet where the market issues one, and a full price.',
      },
      {
        title: 'Confirm in writing',
        text: 'The quotation sets out the CIF price and URA assessment separately. Review it, ask any questions, and give Kabex a clear go-ahead before the import begins.',
      },
      {
        title: 'Pay and take delivery',
        text: `After your go-ahead, the agreed start payment is ${DEPOSIT_RANGE} of the CIF price. The balance is due ${BALANCE_DEADLINE} and may be paid in instalments. Your order records the agreed CIF destination and the services included in your package.`,
      },
    ],
    facts: [
      {
        icon: 'route',
        label: 'Source markets',
        value: 'Worldwide',
      },
      { icon: 'verified', label: 'Every car', value: 'Imported to your spec' },
      { icon: 'payment', label: 'To start an import', value: `${DEPOSIT_RANGE} of the CIF price` },
      { icon: 'registration', label: 'Registration', value: 'Included when listed in your package' },
    ],
    faqs: [
      {
        question: 'Why import rather than buy from a bond in Kampala?',
        answer: `A car in a bond has already been bought by somebody else, who carried the risk and waiting and priced them in. Importing lets you choose the specification. Your quotation states whether registration is included and how the vehicle will be handed over at the agreed CIF destination.`,
      },
      {
        question: 'Can I see the car before I pay for it?',
        answer:
          'Not the car itself. It is still abroad when you choose it, because we source each order rather than keeping cars in a Kampala lot. What you get instead is the auction sheet, inspection report and photographs of that exact unit — and you are welcome at Crown House on Kampala Road to go through them with the officer handling your import.',
      },
      {
        question: 'Where will I receive my car?',
        answer:
          'You and Kabex agree the CIF destination before the order is confirmed. Depending on your order, this may be Mombasa, Kabex’s Kampala office, Nimule, Bunagana or another agreed destination. Your quotation sets out whether clearing and inland transport are included.',
      },
      {
        question: 'What if the car arrives and it is not what was described?',
        answer:
          'Tell your Kabex contact as soon as possible. The agreed specification is in writing, and your order records the CIF destination and handover arrangements.',
      },
    ],
    tool: { label: 'See the cars we can import', to: '/cars' },
    whatsappMessage: 'Hello Kabex Motors, I am looking to import a car.',
  },

  'import-consultation': {
    slug: 'import-consultation',
    title: 'Import consultation',
    icon: 'consult',
    pillar: 'find',
    tagline: 'Ask everything before you commit, not after the car lands.',
    outcome: 'A decision you can defend — including, sometimes, the decision not to import.',
    cost: FREE,
    summary:
      'A conversation with someone who imports for a living, about whether the car you have in mind is the right one to bring into Uganda at all — and what it will actually cost on the road.',
    metaDescription:
      'Free import advice from Kabex Motors before you commit: which years Uganda still allows, whether a model is worth importing, what parts cost here, and the full landed cost.',
    overview: [
      'Most expensive import mistakes are made before any money moves. A model that is cheap in Japan can be unserviceable in Kampala. A year that looks fine on the auction sheet can be a year URA charges an environmental levy on. A saloon that suits the city can be the wrong car for the road to the village.',
      'Import consultation is the conversation that catches those things. It is not a sales call with a different name — a consultation that ends in us telling you not to import that car has done its job, and we would far rather say so now than explain it to you at the port.',
      'It costs nothing and it commits you to nothing, and both of those are deliberate rather than generous. A buyer who understands the full cost before committing is a better customer than one who finds out at Mombasa, and a trade full of surprised buyers is worse for everyone in it.',
    ],
    includes: [
      'Which years are still allowed into Uganda, and which attract an environmental levy',
      'Whether parts and servicing for a model are realistically available in Uganda',
      'Honest advice on models we have seen arrive badly, whoever is selling them',
      'What the car will cost on the road, not just CIF — taxes, transport, registration',
      'Which source market suits the model, and what each market ships with',
      'A second opinion on a car you are buying from somebody else entirely',
      'A written summary of what we discussed, so you are not relying on memory',
    ],
    steps: [
      {
        title: 'Tell us what you are considering',
        text: 'A model, a shortlist, or just a budget and what you need the car to do. All three are a usable starting point.',
      },
      {
        title: 'We check it against the rules',
        text: 'Age limits, the environmental levy band, and how URA values that model and year in its own database.',
      },
      {
        title: 'We tell you what we would do',
        text: 'Including when the answer is a different model, a different year, or not importing at all.',
      },
      {
        title: 'You decide with the numbers in front of you',
        text: 'If you want to proceed we move to a quotation. If not, nothing is owed and nothing has started.',
      },
    ],
    facts: [
      { icon: 'price', label: 'Cost', value: 'Free, and not conditional on buying' },
      { icon: 'calendar', label: 'Passenger age limit', value: '15 years at importation' },
      { icon: 'chat', label: 'Where', value: 'WhatsApp, phone, or our Kampala Road office' },
    ],
    faqs: [
      {
        question: 'What is the oldest car I can import into Uganda?',
        answer:
          'URA currently lists a 50% environmental levy for passenger vehicles aged 9–15 years; vehicles older than 15 years are not allowed. In a free consultation, we check the current rule and tax treatment for the specific vehicle before you commit.',
      },
      {
        question: 'Do I have to buy through you after a consultation?',
        answer:
          'No. The consultation is free and carries no obligation. People use it to sanity-check a car they are buying elsewhere, and that is a legitimate use of it.',
      },
      {
        question: 'Can you advise on a car I have already bought?',
        answer:
          'Yes, and it is worth asking early. If it is already purchased we focus on clearing it properly and what the tax position will be, which is a different conversation but still a useful one.',
      },
    ],
    tool: { label: 'Read the import guide', to: '/import-guide' },
    whatsappMessage: 'Hello Kabex Motors, I would like advice on importing a car before I commit.',
  },

  /* ---------------------------------------------------------------- ship */

  'clearing-forwarding-and-logistics': {
    slug: 'clearing-forwarding-and-logistics',
    title: 'Clearing, forwarding and logistics',
    icon: 'clearing',
    pillar: 'ship',
    tagline: 'Choose the agreed destination and package before the import begins.',
    outcome: 'A written order that names the CIF destination and the services included.',
    cost: QUOTED,
    summary:
      'Clearing, forwarding and inland transport can be arranged as part of your package. The agreed CIF destination and included services are set out in your quotation.',
    metaDescription:
      'Car clearing, forwarding and logistics by Kabex Motors. Your quotation states the agreed CIF destination and whether clearing and inland transport are included.',
    overview: [
      'Clearing is where imports go wrong, and they go wrong for a structural reason. The journey crosses two revenue authorities, a port, a border and several hundred kilometres of road, and it is usually sold to the buyer as four separate services by four parties who each answer only for their own leg. When the car sits, nobody is responsible for the fact that it is sitting.',
      'The services after the vessel arrives depend on the package you choose. Your quotation names the agreed CIF destination and states whether Kabex will handle customs clearing, transit and inland transport.',
      'Some customers arrange to receive the vehicle at Mombasa; others agree on Kabex’s Kampala office, Nimule, Bunagana or another destination. Kabex does not operate a vehicle warehouse. The vehicle is handed over at the destination recorded in your order, once the services included in that package are complete.',
    ],
    includes: [
      'Customs entry and port handling when included in your package',
      'Transit documents and border crossing when included in your package',
      'UNBS roadworthiness inspection when included in your package',
      'Inland transport to the CIF destination agreed in your order, when included in your package',
      'Handover to you at the agreed destination when the included services are complete',
      'Written updates on the stages Kabex handles',
      'Clearing taken on as a standalone job, for cars we had nothing to do with buying',
    ],
    steps: [
      {
        title: 'Documents while the car sails',
        text: 'For packages that include clearing, the bill of lading, export certificate and invoice are collected and checked during the voyage.',
      },
      {
        title: 'Entry and assessment',
        text: 'When clearing is part of your package, Kabex lodges the customs entry and shares URA’s assessment with you.',
      },
      {
        title: 'Duty settled, car released',
        text: `Once the assessment is paid the car is released from the port. Nothing moves before this, which is why the balance falls due ${PORT_ARRIVAL}.`,
      },
      {
        title: 'Included onward services',
        text: 'If your package includes onward transport, the vehicle travels to the agreed CIF destination by the route set out in your order.',
      },
      {
        title: 'Handover at the agreed destination',
        text: 'Kabex hands the vehicle over to you at the CIF destination recorded in your order, after the services included in your package are complete.',
      },
    ],
    facts: [
      {
        icon: 'location',
        label: 'Port of entry',
        value: 'Mombasa · Dar es Salaam · Zanzibar among others',
      },
      { icon: 'route', label: 'Border crossings', value: 'Malaba · Mutukula' },
      {
        icon: 'clearing',
        label: 'Delivery points',
        value: 'Kampala · Bunagana · Nimule · Katuna among others',
      },
      {
        icon: 'tax',
        label: 'Tax basis',
        value: 'Determined by URA’s valuation and vary by vehicle and applicable tax rates',
      },
    ],
    faqs: [
      {
        question: 'How long does clearing take once the car arrives?',
        answer: `Where clearing is included in your package, timing depends on complete documents and payment of the URA assessment. Your quotation states which clearing and transport services Kabex will handle. The balance is due ${PORT_ARRIVAL}.`,
      },
      {
        question: 'Why is the tax more than I calculated from what I paid?',
        answer:
          'Because URA does not tax your invoice. It values the vehicle from its own Used Motor Vehicle Valuation Database, by model and year, converted at a customs rate URA sets itself. Two buyers who paid very different prices for the same model and year are usually charged the same duty. Our calculators use that same valuation list, which is why their estimate starts from the right number.',
      },
      {
        question: 'If my package includes transport, is the car driven to Kampala on its own wheels?',
        answer:
          'No. When inland transport is included, the vehicle goes on a carrier. Your quotation sets out the route and transport included in your package.',
      },
      {
        question: 'Can you clear a car I imported through someone else?',
        answer:
          'Yes. We take on clearing and transport as a standalone service. Bring the bill of lading and the export documents and we will tell you where it stands before we quote.',
      },
    ],
    tool: { label: 'Estimate import costs', to: '/landed-cost-calculator' },
    whatsappMessage: 'Hello Kabex Motors, I need help clearing a vehicle from Mombasa.',
  },

  'local-vehicle-registration': {
    slug: 'local-vehicle-registration',
    title: 'Local vehicle registration',
    icon: 'registration',
    pillar: 'ship',
    tagline: 'Ugandan number plates and a logbook in your own name.',
    outcome: 'A car that is legally yours here — first registration, not a transfer from us.',
    cost: {
      tone: 'quoted',
      label: 'Quoted per job',
      note: 'Government fees are fixed; the Drive-Home Cost calculator carries them',
    },
    summary:
      'First-time registration here in Uganda, run across URA, UNBS and the Ministry of Works — from the tax declaration through to digital number plates and a logbook issued in your own name.',
    metaDescription:
      'Local vehicle registration in Uganda with Kabex Motors: URA declaration, UNBS verification, digital number plates and a logbook issued in your own name, not transferred from ours.',
    overview: [
      'Registration is the step that turns a cleared import into a car you own. It runs locally, across three bodies — URA, UNBS and the Ministry of Works — each with its own queue and its own document it will not proceed without, and it is tracked through URA’s own portal from the clearing agent stage to final ministry approval.',
      `We run it for you, and we run it in your name. ${ownership.body}`,
      'That last point is worth separating out, because it is the one a buyer discovers too late. A car registered first to a dealer and then transferred to you is a car with a previous owner on its record and a transfer fee on your bill. First registration has neither. Ours is first registration.',
    ],
    includes: [
      'The URA declaration and tax payment, made against your own TIN',
      'UNBS roadworthiness verification',
      'Digital number plate application and issue',
      'Stamp duty and registration fees settled on your behalf',
      'Registration in your own name from the start — not a transfer from ours', // NEEDS KABEX
      'The logbook and plates handed to you, with every receipt',
    ],
    steps: [
      {
        title: 'We collect what the process needs',
        text: 'Your TIN and identification, the export certificate, the bill of lading and the customs documents.',
      },
      {
        title: 'Declaration and taxes',
        text: 'The vehicle is declared to URA and the assessed taxes are paid. Registration cannot begin before this is settled.',
      },
      {
        title: 'Inspection and approval',
        text: 'UNBS verifies roadworthiness and the Ministry of Works approves the registration.',
      },
      {
        title: 'Plates and logbook',
        text: 'Digital plates are issued and fitted, and the registration is handed over in your name with the receipts.',
      },
    ],
    facts: [
      { icon: 'registration', label: 'Registered to', value: 'You, from the start' },
      { icon: 'car', label: 'Plates', value: 'Digital, first-time registration' },
      { icon: 'building', label: 'Bodies involved', value: 'URA · UNBS · Ministry of Works' },
    ],
    faqs: [
      {
        question: 'What does registration cost?',
        answer:
          'The registration fee, the digital number plates and stamp duty are fixed government charges, and personalised plates cost substantially more than standard ones. They are all built into the Drive-Home Cost calculator, which reads the current figures rather than quoting a number that will date.',
      },
      {
        question: 'Do I need a TIN to register a car in Uganda?',
        answer:
          'Yes. The declaration is made against a TIN, so you will need one before the vehicle can be declared. If you do not have one we will tell you at the start rather than at the queue.',
      },
      {
        question: 'Can I choose my number plate?',
        answer:
          'A personalised plate can be applied for, at a considerably higher fee than a standard issue. A standard plate is allocated, not chosen.',
      },
      {
        question: 'Why is it called “local” registration?',
        answer:
          'Because it happens here. The car is registered in Uganda, against your own TIN, through URA, UNBS and the Ministry of Works — not registered abroad and brought in on foreign plates. A vehicle in transit to DR Congo or South Sudan is a different case, and we will tell you which one yours is before anything is lodged.',
      },
    ],
    tool: { label: 'Estimate your drive-home cost', to: '/landed-cost-calculator' },
    whatsappMessage: 'Hello Kabex Motors, I need help registering a vehicle in Uganda.',
  },

  /* ----------------------------------------------------------------- pay */

  'tax-inquiries-and-quotations': {
    slug: 'tax-inquiries-and-quotations',
    title: 'Free tax inquiries and quotations',
    icon: 'quote',
    pillar: 'pay',
    tagline: 'What URA will charge, in writing, before you spend anything.',
    outcome: 'One number you can budget against — and hold us to.',
    cost: FREE,
    summary:
      'A written quotation covering the car, shipping and URA taxes, with clearing, transport and registration listed where they are included in your package.',
    metaDescription:
      'Free car import tax inquiries and written quotations from Kabex Motors, with package-dependent clearing, transport and registration costs set out before you commit.',
    overview: [
      'The single most common way a Ugandan import goes wrong is a buyer who budgeted for the car and not for the car landed. The taxes are not a surcharge on the purchase price — they are assessed on URA’s own valuation of the model and year, and are frequently of the same order as the vehicle itself.',
      'A tax inquiry is free and it is unconditional. Ask us about a car you are buying from somebody else and we will still answer, because a buyer who is surprised at the port is bad for the whole trade.',
      'What comes back is one total with its parts shown separately, so you can see which line is a government charge and which line is ours. That separation is the point: a single figure you cannot break down is a figure you cannot check.',
    ],
    includes: [
      'The URA taxes and levies that will apply to your specific model and year',
      'How URA values that vehicle, which is not what you paid for it',
      'Shipping, taxes and any clearing or inland transport included in your package',
      'Registration, plates and stamp duty',
      'One total: what the car costs you on the road',
      'Every line shown separately, so you can see what is a government charge and what is ours',
      'A written quotation you can hold us to, and plan a budget around',
    ],
    steps: [
      {
        title: 'Send us the vehicle',
        text: 'Make, model, year and engine size. An auction sheet or a listing link is ideal; the year matters most.',
      },
      {
        title: 'We value it as URA will',
        text: 'Against the URA valuation database for that model and year, at the customs exchange rate URA applies.',
      },
      {
        title: 'We add everything else',
        text: 'Shipping, duties and levies, plus any transport, registration or plates included in your package.',
      },
      {
        title: 'You get one number, in writing',
        text: 'With the parts shown separately, so you can see what is a government charge and what is ours.',
      },
    ],
    facts: [
      { icon: 'price', label: 'Cost', value: 'Free' },
      { icon: 'tax', label: 'Basis', value: "URA's valuation database" },
      { icon: 'calculator', label: 'Covers', value: 'Car, shipping, taxes, transport, plates' },
    ],
    faqs: [
      {
        question: 'Why can you not tell me the exact tax?',
        answer:
          "Because the final figure is URA's to assess, not ours, and it moves with the valuation list and the customs exchange rate. What we give you is an estimate built from the same valuation database URA uses, which is as close as anyone can get before the entry is lodged. Your final tax must be confirmed through official URA channels.",
      },
      {
        question: 'Is the quotation really free?',
        answer:
          'Yes, and it is not conditional on importing through us. It is one of the services Kabex lists, and it has been free since 2018.',
      },
      {
        question: 'Can I work it out myself?',
        answer:
          'Yes — the tax calculator on this site runs the same figures, and the Drive-Home Cost calculator adds transport and registration on top. Use those for a quick answer and ask us when you want it in writing.',
      },
    ],
    tool: { label: 'Open the tax calculator', to: '/tax-calculator' },
    whatsappMessage:
      'Hello Kabex Motors, I would like a free tax inquiry and quotation for a vehicle.',
  },

  /**
   * The merged record (2026-10-21). See the file header for why `tax-finance`
   * and `payment-support` became one page rather than two.
   *
   * It is ordered the way the money is actually spent, which is the whole
   * argument for the merge: the quotation, the deposit, the instalments while
   * the car is at sea, the balance before it lands, and then the duty at
   * clearing. Read in that order it is one subject. Read as two pages it was
   * two halves of a sentence filed under different headings.
   */
  'tax-financing': {
    slug: 'tax-financing',
    title: 'Tax financing',
    icon: 'finance',
    pillar: 'pay',
    tagline: 'Every shilling an import costs you, planned — the car, and the duty at clearing.',
    outcome:
      'A finance partner can help you pay URA taxes and other agreed fees. Kabex charges no service fee for arranging this help.',
    cost: NO_KABEX_FEE,
    summary: `Plan for the car and the URA assessment together. Pay ${DEPOSIT_RANGE} of the CIF price to start, spread the balance in agreed instalments while the car is at sea, and ask Kabex about help arranging tax finance through trusted partners if the duty is more than you can clear at once.`,
    metaDescription: `Plan your car import payments with Kabex Motors, and ask about help arranging finance through trusted partners for the URA duty assessment.`,
    overview: [
      'An import is two bills and one cost. The first is the car — quoted as CIF, payable to us. The second is the URA assessment, which lands when the vehicle does and is frequently of the same order as the vehicle itself. Treat them as one budget from the start and an import is a plan. Treat them as two surprises and it is a car at Mombasa accruing charges.',
      deposit.body,
      balance.body,
      'Then the duty, which arrives when the car reaches port. We review URA’s assessment first, because an incorrect year or vehicle classification should be resolved before you take on financing. If you cannot clear the taxes and other agreed costs at once, Kabex can help you explore funding through trusted finance partners. Availability, approval, fees and repayment terms depend on the partner and are explained before you decide.',
      'The last part is making sure your money goes where it should. Every payment is against an invoice, into the company account, and receipted. Nothing is payable through this website, and no member of our team will ever ask you to send money to a personal account.',
    ],
    includes: [
      deposit.medium,
      'The balance payable in instalments across the weeks your car is at sea',
      'A written instalment plan, agreed before the first payment rather than as you go',
      'A review of the URA assessment before anything is arranged, in case it can be reduced legitimately',
      'Help exploring finance partner options for taxes and other agreed costs, with the partner’s terms explained before you decide',
      'Coordination with the clearing team, so release follows payment immediately',
      'An invoice before payment and an emailed e-receipt after each recorded instalment',
      'Payment to the Kabex Motors company account only',
      'A statement of what you have paid and what is outstanding, whenever you ask',
      'The customs exchange rate explained, so the shilling figure does not surprise you',
      'Guidance for buyers paying from outside Uganda',
      'An honest answer when financing the duty is the wrong move for you',
    ],
    steps: [
      {
        title: 'Quotation first',
        text: 'The CIF price and URA assessment are set out separately, along with the start payment for your car. Kabex does not begin the import until you give the go-ahead.',
      },
      {
        title: `${DEPOSIT_RANGE} to start`,
        text: 'After your go-ahead, pay the start amount stated on your quotation against an invoice. Kabex then proceeds with the import.',
      },
      {
        title: 'The balance, at your pace',
        text: balance.instalmentOption,
      },
      {
        title: 'Settled before it lands',
        text: `The balance is due ${BALANCE_DEADLINE}, so the duty can be settled and the car released the week it arrives instead of accruing charges at the port.`,
      },
      {
        title: 'The assessment, checked',
        text: 'URA assesses the duty on its own valuation. We read it for errors before anything else — a wrong year or a misclassified body is worth more to you than any financing arrangement.',
      },
      {
        title: 'Duty met, car released',
        text: 'What can be arranged to meet it, what it costs and what it commits you to, in writing before you decide. The clearing team is told the moment it is paid.',
      },
    ],
    facts: [
      { icon: 'payment', label: 'To start', value: `${DEPOSIT_RANGE} of the CIF price` },
      { icon: 'finance', label: 'The balance', value: 'In instalments, if you prefer' },
      { icon: 'calendar', label: 'Balance due', value: 'Two weeks before it reaches port' },
      { icon: 'tax', label: 'Duty support', value: 'Partner options explained before you decide' },
      { icon: 'building', label: 'Paid to', value: 'The Kabex Motors company account' },
      { icon: 'verified', label: 'Every payment', value: 'Invoiced and receipted' },
      { icon: 'fraud', label: 'Through this website', value: 'Never' },
    ],
    faqs: [
      {
        question: 'Can I pay in instalments?',
        answer: `Yes. ${installments.body} The import begins only after you review the quotation and give a clear go-ahead. The balance can then be paid down in stages, provided it is clear ${BALANCE_DEADLINE}.`,
      },
      {
        question: 'Can I request a refund if my plans change?',
        answer:
          'Yes. You may submit a refund request at any time. Kabex reviews each request and confirms the approved amount before payout; a request does not guarantee that the full amount paid will be refunded.',
      },
      {
        question: `Why is the deposit ${DEPOSIT_RANGE} rather than one figure?`,
        answer:
          'Because it depends on the car. Some units are committed to on different terms than others, and rather than quote you the higher figure as a matter of course, we tell you which one applies to the specific car you have chosen. It is on your quotation in writing before you pay anything, and it does not change afterwards.',
      },
      {
        question: 'How does tax financing work?',
        answer:
          'If you cannot clear the taxes and other agreed costs at once, Kabex can help you explore funding through trusted finance partners. The partner assesses the request and provides its own approval decision, fees and repayment terms. Review the full written terms before accepting; finance is not guaranteed.',
      },
      {
        question: 'How much does help with the duty cost?',
        answer:
          'Partner fees and repayment terms vary by the option and the assessment. Ask for the full written terms, including the total amount repayable and payment dates, before deciding whether to proceed.',
      },
      {
        question: 'Can I avoid needing help with the duty at all?',
        answer:
          'Usually, and it is the better outcome. The reason this site has two calculators and gives quotations free is so the duty is in your budget from the start rather than a surprise at the port. Ask for a quotation before you buy.',
      },
      {
        question: 'What happens if I cannot pay the balance in time?',
        answer:
          'Tell us as early as possible. Kabex can review the situation and help explore finance partner options for taxes and other agreed costs. Approval is up to the partner, and any fees and repayment terms should be clear to you before you accept.',
      },
      {
        question: 'How do I know a payment request is really from Kabex?',
        answer:
          'Because it is an invoice, in the company name, naming the company account. We do not collect money through this website and we do not take payment to personal accounts. If a request looks different, call the office number on this site before you send anything.',
      },
    ],
    tool: { label: 'Estimate your taxes first', to: '/tax-calculator' },
    whatsappMessage:
      'Hello Kabex Motors, I would like to understand your payment terms and tax financing.',
  },
};

/**
 * Every §7 name mapped to the page that covers it.
 *
 * This exists only to be type-checked, and that is enough reason for it: a
 * `Record` over `ServiceTitle` cannot be built without an entry for every
 * supplied name, so adding a service to `site.ts` makes this a compile error
 * until the catalogue above has a record for it. Without it the drift is
 * silent — the new service would show up in the home strip and the /about
 * band and simply be missing from /services.
 */
const titleToSlug: Record<ServiceTitle, ServiceSlug> = {
  'Car importation': 'car-importation',
  'Tax financing': 'tax-financing',
  'Local vehicle registration': 'local-vehicle-registration',
  'Import consultation': 'import-consultation',
  'Clearing, forwarding and logistics': 'clearing-forwarding-and-logistics',
  'Free tax inquiries and quotations': 'tax-inquiries-and-quotations',
};

/**
 * The catalogue in the order the hub lays it out: by pillar, and within a
 * pillar in the order the records are declared above.
 *
 * Derived rather than typed out again. The declaration order inside `bySlug`
 * is already grouped by pillar — the section comments mark it — so this sort
 * is a safety net for the day someone moves a record and forgets, not the
 * thing that establishes the order.
 */
export const serviceCatalogue: readonly Service[] = servicePillars.flatMap((pillar) =>
  Object.values(bySlug).filter((service) => service.pillar === pillar.id),
);

/** The service a `/services/:slug` URL names, or undefined if it names none. */
export function findService(slug: string): Service | undefined {
  return Object.hasOwn(bySlug, slug) ? bySlug[slug as ServiceSlug] : undefined;
}

/** The path of a service's own page. One place, so no caller builds it by hand. */
export const servicePath = (service: Pick<Service, 'slug'>) => `/services/${service.slug}`;

/** The page covering a supplied §7 name — what the home strip and /about link to. */
export const servicePathForTitle = (title: ServiceTitle) => `/services/${titleToSlug[title]}`;

/**
 * Every service path, for the prerender list and the sitemap.
 *
 * Derived, and that is the point of exporting it: until 2026-10-21 the hub
 * and all of its detail pages were missing from `react-router.config.ts`
 * entirely, so a site that prerenders every page shipped its service pages as
 * an empty shell and left them out of the sitemap. A hand-maintained list
 * would have been the same bug with a longer fuse.
 */
export const servicePaths: readonly string[] = [
  '/services',
  ...serviceCatalogue.map((service) => servicePath(service)),
];
